import { date } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import {
  type GlobalStateModel,
  useGlobalStateContextSelectors,
} from 'components/GlobalState/GlobalStateModel';
import { RequireSnapshot } from 'containers/App/RequireSnapshot/RequireSnapshot';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { PermissionsRequiredPage } from 'pages/PermissionsRequired/PermissionsRequiredPage';
import { type FC, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigatePushPath } from 'utils/history/useNavigatePushPath';
import type { PageAccessRestriction } from 'utils/permissions/PageAccessRestriction';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';
import { useHasPermission } from 'utils/permissions/useHasPermission';

type Props = PageAccessRestriction & {
  children: ReactNode;
};

const licenseIsValid = (s: GlobalStateModel) =>
  // validTo is null before user authorizes himself, so we consider the license valid.
  // Check for validTo is there for long-open UI - when license expires, users would otherwise
  // be able to have the browser tab open for long and still use non-restricted backend features.
  (s.license.validTo ?? Number.MAX_SAFE_INTEGER) - date.getTs() > 1 &&
  s.license.state === 'VALID';

export const RestrictedPageAccess: FC<Props> = (props) => {
  const ctxProps = useGlobalStateContextSelectors({
    licenseOk: (s) => !props.requiredLicense || licenseIsValid(s),
    loginStatus: prop('loginStatus'),
    loginOk: (s) => !props.requiredAuth || s.loginStatus === 'AUTHENTICATED',
  });

  const navigatePushPath = useNavigatePushPath();
  const hasPermission = useHasPermission();
  const apiScopeRequirementMet = useApiScopeRequirementMet();

  const legacyScopesOk = hasPermission(props.requiredPermissions ?? []);
  const apiScopesOk =
    props.requiredApiScope === undefined ||
    apiScopeRequirementMet(props.requiredApiScope);

  // redirect when license or login check does not pass
  useEffect(() => {
    if (!ctxProps.licenseOk) {
      navigatePushPath('/upload-license');
    } else if (!ctxProps.loginOk && ctxProps.loginStatus !== 'LOGGING_IN') {
      // Do not redirect when logging in. Login status change is propagated to global state context
      // sometimes few ticks later than user is redirected to restricted page, causing immediate logout.
      navigatePushPath('/logout');
    }
  }, [ctxProps.licenseOk, ctxProps.loginStatus, ctxProps.loginOk]);

  if (!ctxProps.loginOk && ctxProps.loginStatus === 'LOGGING_IN') {
    return <LoaderAndText />;
  }

  if (!legacyScopesOk || !apiScopesOk) {
    return <PermissionsRequiredPage />;
  }

  if (!ctxProps.licenseOk || !ctxProps.loginOk) {
    // shouldn't be shown for long, redirect should be performed, we want to prevent blinking
    // any misleading error message before the redirect finishes.
    return null;
  }

  if (props.requiredSnapshot) {
    return <RequireSnapshot>{props.children}</RequireSnapshot>;
  }
  return props.children;
};
